import React, { useEffect, useRef, useState, useCallback } from "react";
import "./Cameras.css";
import axios from "axios";
import { useSnackbar } from "notistack";
import { loadPlayer } from "rtsp-relay/browser";

// MUI components
import {
  Card,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Paper,
  Box,
  Button
} from "@mui/material";

// Icons
import VideocamIcon from "@mui/icons-material/Videocam";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Stream config & Modal
import {
  cleanPrefixName,
  formatDeviceId,
  getDeviceStreamUrl,
  getDevicePublishAudioUrl
} from "../../config/streams";
import LiveStreamModal from "../../components/LiveStreamModal/LiveStreamModal";

const STREAMS = [
  { id: "video", label: "Video" },
  { id: "thermal", label: "Thermal" },
];

function Cameras() {
  const { enqueueSnackbar } = useSnackbar();
  const auth = localStorage.getItem("authToken");
  const userId = localStorage.getItem("user_id") || "1";

  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const inlineAudioRef = useRef(null);

  const [activeTab, setActiveTab] = useState("video");
  const [streamPrefix, setStreamPrefix] = useState(() => localStorage.getItem("stream_prefix") || "Cam");
  const [prefixesList, setPrefixesList] = useState([
    { prefix_id: 1, prefix_name: "Cam", label: "Cam", is_default: 1 },
    { prefix_id: 2, prefix_name: "Smartlight", label: "Smartlight", is_default: 1 },
  ]);

  const [isAddPrefixOpen, setIsAddPrefixOpen] = useState(false);
  const [isManagePrefixOpen, setIsManagePrefixOpen] = useState(false);
  const [newPrefixName, setNewPrefixName] = useState("");
  const [newPrefixLabel, setNewPrefixLabel] = useState("");
  const [prefixSubmitting, setPrefixSubmitting] = useState(false);

  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCamId, setSelectedCamId] = useState("");
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [subdataMap, setSubdataMap] = useState({});
  const [vlcModalOpen, setVlcModalOpen] = useState(false);

  const [isInlinePlaying, setIsInlinePlaying] = useState(true);
  const [isInlineMuted, setIsInlineMuted] = useState(false);
  const [isPublishingAudio, setIsPublishingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const pubWsRef = useRef(null);
  const micStreamRef = useRef(null);
  const prevMotionRef = useRef({});

  // 1. Fetch Stream Prefixes from Backend
  const fetchPrefixes = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL || "http://localhost:8080";
      const res = await axios.get(`${baseUrl}/admin/stream/prefixes`);
      if (res.data && res.data.success && Array.isArray(res.data.prefixes)) {
        setPrefixesList(res.data.prefixes);
      }
    } catch (err) {
      console.warn("Using default prefixes, backend fetch error:", err);
    }
  }, []);

  // 2. Fetch Devices List & Telemetry Subdata
  const fetchDevicesAndSubdata = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoadingDevices(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL || "http://localhost:8080";

      // Devices
      const devRes = await axios.post(
        `${baseUrl}/admin/device/getalldevice`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      let devs = [];
      if (devRes.data && Array.isArray(devRes.data.areas)) {
        devs = devRes.data.areas.map((d) => ({
          id: d.camera_name || d.device_name || `VC-${String(d.id || d.device_id).padStart(3, "0")}`,
          name: d.camera_name || d.device_name || `Device #${d.id || d.device_id}`,
          device_id: d.id || d.device_id,
          camera_name: d.camera_name,
        }));
        setCameraDevices(devs);
        if (!selectedCamId && devs.length > 0) {
          setSelectedCamId(devs[0].id);
        }
      }

      // Subdata Telemetry
      const subRes = await axios.post(
        `${baseUrl}/admin/users/getsubdata`,
        { ticket_id: "" },
        { headers: { Authorization: `Bearer ${auth}` } }
      );

      if (subRes.data) {
        const rawList = Array.isArray(subRes.data)
          ? subRes.data
          : Array.isArray(subRes.data.data)
          ? subRes.data.data
          : [];

        const map = {};
        rawList.forEach((item) => {
          const k = String(item.device || item.Cam_ID || item.cam_id || item.device_id || "").trim();
          if (k) map[k] = item;
        });
        setSubdataMap(map);

        // Motion notification sound/alert
        rawList.forEach((item) => {
          const k = String(item.device || item.Cam_ID || item.cam_id || item.device_id || "").trim();
          const isMotionNow = item.motion === true || item.motion === "true" || item.event === "motion_detected";
          const wasMotionBefore = prevMotionRef.current[k];

          if (isMotionNow && !wasMotionBefore) {
            enqueueSnackbar(`🚨 Motion detected on Camera/Device #${k}!`, {
              variant: "error",
              autoHideDuration: 4000,
            });
          }
          prevMotionRef.current[k] = isMotionNow;
        });
      }
    } catch (err) {
      console.error("Error fetching devices/subdata for cameras:", err);
    } finally {
      if (!isSilent) setLoadingDevices(false);
    }
  }, [userId, auth, selectedCamId, enqueueSnackbar]);

  useEffect(() => {
    fetchPrefixes();
    fetchDevicesAndSubdata(false);
    const interval = setInterval(() => {
      fetchDevicesAndSubdata(true);
    }, 2500);
    return () => clearInterval(interval);
  }, [fetchPrefixes, fetchDevicesAndSubdata]);

  // Handle Add Prefix
  const handleAddPrefix = async () => {
    const cleanName = cleanPrefixName(newPrefixName);
    if (!cleanName || (cleanName === "Cam" && newPrefixName !== "Cam")) {
      enqueueSnackbar("Please enter a valid prefix name (alphanumeric, -, _)", { variant: "warning" });
      return;
    }

    setPrefixSubmitting(true);
    try {
      const baseUrl = process.env.REACT_APP_BASEURL || "http://localhost:8080";
      const res = await axios.post(`${baseUrl}/admin/stream/prefixes/add`, {
        prefix_name: cleanName,
        label: newPrefixLabel.trim() || cleanName,
      });

      if (res.data && res.data.success) {
        enqueueSnackbar(`Stream prefix "${cleanName}" added!`, { variant: "success" });
        setStreamPrefix(cleanName);
        localStorage.setItem("stream_prefix", cleanName);
        setIsAddPrefixOpen(false);
        setNewPrefixName("");
        setNewPrefixLabel("");
        fetchPrefixes();
      } else {
        enqueueSnackbar(res.data.message || "Error adding prefix", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to connect to backend server", { variant: "error" });
    } finally {
      setPrefixSubmitting(false);
    }
  };

  // Handle Delete Prefix
  const handleDeletePrefix = async (prefix) => {
    try {
      const baseUrl = process.env.REACT_APP_BASEURL || "http://localhost:8080";
      const res = await axios.post(`${baseUrl}/admin/stream/prefixes/delete`, {
        prefix_id: prefix.prefix_id,
        prefix_name: prefix.prefix_name,
      });

      if (res.data && res.data.success) {
        enqueueSnackbar(`Prefix "${prefix.prefix_name}" deleted`, { variant: "success" });
        if (streamPrefix === prefix.prefix_name) {
          setStreamPrefix("Cam");
          localStorage.setItem("stream_prefix", "Cam");
        }
        fetchPrefixes();
      } else {
        enqueueSnackbar(res.data.message || "Cannot delete prefix", { variant: "error" });
      }
    } catch (err) {
      enqueueSnackbar("Failed to delete prefix", { variant: "error" });
    }
  };

  // Target Device details
  const selectedCamObj = cameraDevices.find((c) => c.id === selectedCamId);
  const targetDeviceId = selectedCamObj
    ? selectedCamObj.device_id || selectedCamObj.id
    : selectedCamId || "10";
  const formattedDevId = formatDeviceId(targetDeviceId);
  const activeStreamUrl = getDeviceStreamUrl(activeTab, targetDeviceId, streamPrefix);
  const inlineAudioUrl = getDeviceStreamUrl("audio", targetDeviceId, streamPrefix);
  const httpInlineAudioUrl = `${process.env.REACT_APP_BASEURL || "http://localhost:8080"}/admin/stream/audio?url=${encodeURIComponent(inlineAudioUrl)}`;
  const publishAudioUrl = getDevicePublishAudioUrl(targetDeviceId, streamPrefix);
  const currentStream = STREAMS.find((s) => s.id === activeTab) || STREAMS[0];

  // Stop Publishing Talkback Audio
  const stopPublishingAudio = () => {
    setIsPublishingAudio(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (micStreamRef.current) {
      try { micStreamRef.current.getTracks().forEach((track) => track.stop()); } catch (e) {}
      micStreamRef.current = null;
    }
    if (pubWsRef.current) {
      try { pubWsRef.current.close(); } catch (e) {}
      pubWsRef.current = null;
    }
  };

  // Start Publishing Talkback Audio
  const startPublishingAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => stopPublishingAudio();
      }

      const rawBase = process.env.REACT_APP_BASEURL || "http://localhost:8080";
      const wsBaseUrl = rawBase.replace(/^http/, "ws");
      const wsUrl = `${wsBaseUrl}/admin/stream/publish-audio?url=${encodeURIComponent(publishAudioUrl)}`;
      const ws = new WebSocket(wsUrl);
      pubWsRef.current = ws;

      ws.onopen = () => {
        let mimeType = "audio/webm;codecs=opus";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = "audio/webm";
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = "";
          }
        }

        const options = mimeType ? { mimeType } : undefined;
        const mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = async (event) => {
          if (event.data && event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            try {
              const buffer = await event.data.arrayBuffer();
              ws.send(buffer);
            } catch (sendErr) {
              console.error("Error sending audio data buffer:", sendErr);
            }
          }
        };

        mediaRecorder.start(500);
        setIsPublishingAudio(true);
      };

      ws.onerror = (err) => {
        console.error("Mic publish error:", err);
        stopPublishingAudio();
      };
      ws.onclose = () => stopPublishingAudio();
    } catch (err) {
      console.error("Error accessing mic for talkback:", err);
      enqueueSnackbar("Microphone access denied or unavailable", { variant: "warning" });
      stopPublishingAudio();
    }
  };

  const togglePublishAudio = () => {
    if (isPublishingAudio) {
      stopPublishingAudio();
    } else {
      startPublishingAudio();
    }
  };

  useEffect(() => {
    return () => stopPublishingAudio();
  }, []);

  // Inline Player Initialization
  const destroyInlinePlayer = () => {
    if (playerRef.current) {
      try {
        if (typeof playerRef.current.destroy === "function") {
          playerRef.current.destroy();
        } else if (typeof playerRef.current.stop === "function") {
          playerRef.current.stop();
        }
      } catch (err) {}
      playerRef.current = null;
    }
    if (inlineAudioRef.current) {
      try {
        inlineAudioRef.current.pause();
        inlineAudioRef.current.src = "";
      } catch (e) {}
    }
  };

  const initInlinePlayer = useCallback(() => {
    destroyInlinePlayer();
    if (!containerRef.current || !targetDeviceId) return;

    containerRef.current.innerHTML = "";
    const canvas = document.createElement("canvas");
    canvas.className = "admin_stream_canvas";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.objectFit = "contain";
    canvas.style.display = "block";
    containerRef.current.appendChild(canvas);

    const rawBase = process.env.REACT_APP_BASEURL || "http://localhost:8080";
    const wsBaseUrl = rawBase.replace(/^http/, "ws");
    const wsUrl = `${wsBaseUrl}/admin/stream/api/stream?url=${encodeURIComponent(activeStreamUrl)}`;

    loadPlayer({
      url: wsUrl,
      canvas: canvas,
      audio: false,
      pauseWhenHidden: false,
      videoBufferSize: 2 * 1024 * 1024,
      onDisconnect: () => {},
    })
      .then((p) => {
        playerRef.current = p;
      })
      .catch((err) => {
        console.error("Error loading inline player:", err);
      });
  }, [activeStreamUrl, targetDeviceId]);

  useEffect(() => {
    if (vlcModalOpen) {
      destroyInlinePlayer();
      return;
    }

    const timer = setTimeout(() => {
      initInlinePlayer();
    }, 100);

    return () => {
      clearTimeout(timer);
      destroyInlinePlayer();
    };
  }, [initInlinePlayer, vlcModalOpen]);

  useEffect(() => {
    if (inlineAudioRef.current) {
      inlineAudioRef.current.muted = isInlineMuted;
      if (!isInlineMuted && isInlinePlaying && inlineAudioRef.current.paused && !vlcModalOpen) {
        inlineAudioRef.current.play().catch((e) => console.warn(e));
      }
    }
  }, [isInlineMuted, isInlinePlaying, vlcModalOpen]);

  useEffect(() => {
    if (selectedCamId && !vlcModalOpen && inlineAudioRef.current) {
      inlineAudioRef.current.src = httpInlineAudioUrl;
      inlineAudioRef.current.muted = isInlineMuted;
      if (isInlinePlaying) {
        inlineAudioRef.current.play().catch((err) => {
          console.warn("Inline audio play error:", err);
        });
      }
    } else if (inlineAudioRef.current) {
      inlineAudioRef.current.pause();
      inlineAudioRef.current.src = "";
    }
  }, [selectedCamId, httpInlineAudioUrl, vlcModalOpen]);

  const toggleInlinePlay = () => {
    if (isInlinePlaying) {
      if (playerRef.current) {
        if (typeof playerRef.current.pause === "function") playerRef.current.pause();
        else if (typeof playerRef.current.stop === "function") playerRef.current.stop();
      }
      if (inlineAudioRef.current) {
        inlineAudioRef.current.pause();
      }
      setIsInlinePlaying(false);
    } else {
      if (playerRef.current) {
        if (typeof playerRef.current.play === "function") playerRef.current.play();
      }
      if (inlineAudioRef.current) {
        inlineAudioRef.current.src = httpInlineAudioUrl;
        inlineAudioRef.current.muted = isInlineMuted;
        inlineAudioRef.current.play().catch((e) => console.warn(e));
      }
      setIsInlinePlaying(true);
    }
  };

  const toggleInlineMute = () => {
    setIsInlineMuted((prev) => !prev);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header bar with Stream Mode toggles and Dynamic Prefix selector */}
        <Card style={{ padding: "16px 20px", marginBottom: "20px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            {/* Stream Modes (Video + Audio, Thermal, Audio) */}
            <Box display="flex" alignItems="center" gap={1}>
              <MDTypography variant="button" fontWeight="bold" color="text" mr={1}>
                Stream Mode:
              </MDTypography>
              {STREAMS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveTab(s.id)}
                  style={{
                    padding: "8px 16px",
                    borderRadius: "20px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    backgroundColor: activeTab === s.id ? "#1976d2" : "#f0f2f5",
                    color: activeTab === s.id ? "#ffffff" : "#444444",
                    boxShadow: activeTab === s.id ? "0 3px 8px rgba(25, 118, 210, 0.3)" : "none",
                    transition: "all 0.2s ease",
                  }}
                >
                  {s.label}
                </button>
              ))}
            </Box>

            {/* Dynamic Stream Prefix Selector (Cam, Smartlight, Pole, etc.) */}
            <Box
              display="flex"
              alignItems="center"
              bgcolor="#ffffff"
              p="4px 12px"
              borderRadius="20px"
              border="1px solid #1976d2"
              gap={1}
            >
              <MDTypography variant="caption" fontWeight="bold" color="text">
                Prefix:
              </MDTypography>
              <select
                value={streamPrefix}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__ADD_NEW__") {
                    setIsAddPrefixOpen(true);
                  } else {
                    setStreamPrefix(val);
                    localStorage.setItem("stream_prefix", val);
                  }
                }}
                style={{
                  border: "none",
                  padding: "4px 8px",
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#1976d2",
                  backgroundColor: "#f5f9ff",
                  borderRadius: "10px",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {prefixesList.map((p) => (
                  <option key={p.prefix_id || p.prefix_name} value={p.prefix_name}>
                    {p.label || p.prefix_name} ({p.prefix_name}-{formattedDevId})
                  </option>
                ))}
                <option value="__ADD_NEW__">➕ Add New Prefix...</option>
              </select>

              <Tooltip title="Add New Stream Prefix">
                <IconButton
                  size="small"
                  onClick={() => setIsAddPrefixOpen(true)}
                  style={{ color: "#1976d2", padding: "4px" }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Manage Stream Prefixes">
                <IconButton
                  size="small"
                  onClick={() => setIsManagePrefixOpen(true)}
                  style={{ color: "#555", padding: "4px" }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Card>

        {/* Live Stream Stage Card */}
        {selectedCamId && (
          <Card style={{ padding: "20px", marginBottom: "24px", backgroundColor: "#1e1e24", color: "#fff" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Chip
                  label="LIVE RTSP"
                  color="error"
                  size="small"
                  style={{ fontWeight: "bold", letterSpacing: "1px" }}
                />
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {selectedCamObj?.name || `Camera #${targetDeviceId}`}
                </MDTypography>
                <Chip
                  label={`${streamPrefix}-${formattedDevId}`}
                  size="small"
                  sx={{ bgcolor: "rgba(25, 118, 210, 0.2)", color: "#90caf9", fontWeight: "bold" }}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Popout VLC Media Player">
                  <IconButton
                    size="small"
                    onClick={() => setVlcModalOpen(true)}
                    style={{ color: "#ff9800", backgroundColor: "rgba(255, 152, 0, 0.15)" }}
                  >
                    <FullscreenIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Video Stage */}
            <Box
              position="relative"
              minHeight="320px"
              maxHeight="480px"
              bgcolor="#000"
              borderRadius="10px"
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <div ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center" }} />
              {/* Synchronized Inline Live Audio Stream */}
              <audio
                ref={inlineAudioRef}
                src={selectedCamId && !vlcModalOpen ? httpInlineAudioUrl : undefined}
                autoPlay
                playsInline
                muted={isInlineMuted}
                style={{ display: "none" }}
              />
            </Box>

            {/* Stage Controls Footer */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={1} borderTop="1px solid #333">
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={toggleInlinePlay} style={{ color: "#fff" }} size="small">
                  {isInlinePlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <IconButton onClick={toggleInlineMute} style={{ color: isInlineMuted ? "#ef5350" : "#fff" }} size="small">
                  {isInlineMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
                <MDTypography variant="caption" color="text" ml={1}>
                  {currentStream.label} &bull; {streamPrefix}-{formattedDevId}
                </MDTypography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <MDButton
                  variant={isPublishingAudio ? "contained" : "outlined"}
                  color={isPublishingAudio ? "error" : "info"}
                  size="small"
                  startIcon={isPublishingAudio ? <MicIcon /> : <MicOffIcon />}
                  onClick={togglePublishAudio}
                >
                  {isPublishingAudio ? "Stop Speaking" : "Talkback (Mic)"}
                </MDButton>
                <IconButton onClick={() => setVlcModalOpen(true)} style={{ color: "#ff9800" }} size="small">
                  <FullscreenIcon />
                </IconButton>
              </Box>
            </Box>
          </Card>
        )}

        {/* Cameras List Grid */}
        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="bold">
            All Surveillance & Camera Devices ({cameraDevices.length})
          </MDTypography>
          <IconButton size="small" onClick={() => fetchDevicesAndSubdata(false)} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </MDBox>

        {loadingDevices ? (
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress />
          </Box>
        ) : cameraDevices.length === 0 ? (
          <Card style={{ padding: "30px", textAlign: "center" }}>
            <MDTypography variant="body2" color="text">
              No camera devices found. Add devices in the Devices section.
            </MDTypography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {cameraDevices.map((cam) => {
              const devIdStr = String(cam.device_id || "");
              const camNameStr = String(cam.camera_name || cam.name || "");
              const subData =
                subdataMap[devIdStr] ||
                subdataMap[camNameStr] ||
                subdataMap[`VC-${devIdStr}`] ||
                subdataMap[`Smartlight-${devIdStr}`] ||
                subdataMap[`Cam-${devIdStr}`] ||
                {};

              const isMotion =
                subData.motion === true ||
                subData.motion === "true" ||
                subData.event === "motion_detected";

              const isSelected = cam.id === selectedCamId;
              const timestampStr = subData.timestamp
                ? new Date(subData.timestamp * (subData.timestamp < 10000000000 ? 1000 : 1)).toLocaleTimeString()
                : "N/A";

              return (
                <Grid item xs={12} sm={6} md={4} key={cam.id}>
                  <div
                    className={`admin_camera_card ${isMotion ? "motion_detected_card" : ""} ${isSelected ? "selected_card" : ""}`}
                    onClick={() => setSelectedCamId(cam.id)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <MDTypography variant="subtitle2" fontWeight="bold" color="dark">
                        {cam.name}
                      </MDTypography>
                      {isMotion ? (
                        <div className="admin_motion_badge">
                          <WarningAmberIcon style={{ fontSize: "14px", marginRight: "3px" }} />
                          MOTION DETECTED
                        </div>
                      ) : (
                        <div className="admin_normal_badge">
                          <CheckCircleIcon style={{ fontSize: "14px", marginRight: "3px" }} />
                          Normal
                        </div>
                      )}
                    </Box>

                    <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                      <VideocamIcon style={{ fontSize: "48px", color: isMotion ? "#d32f2f" : isSelected ? "#1976d2" : "#757575" }} />
                      <MDTypography variant="caption" color="text" mt={1}>
                        Stream: <strong>{streamPrefix}-{formatDeviceId(cam.device_id)}</strong>
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Event: {subData.event || (isMotion ? "motion_detected" : "motion_cleared")}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" fontSize="10px">
                        Last Active: {timestampStr}
                      </MDTypography>
                    </Box>

                    <Box display="flex" justifyContent="space-between" alignItems="center" pt={1} borderTop="1px solid #f0f0f0">
                      <Chip
                        label={isSelected ? "Streaming Now" : "Click to Stream"}
                        size="small"
                        color={isSelected ? "info" : "default"}
                        sx={{ fontSize: "10px", height: "20px" }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCamId(cam.id);
                          setVlcModalOpen(true);
                        }}
                        style={{ fontSize: "11px", color: "#ff9800", textTransform: "none" }}
                      >
                        Popout VLC
                      </Button>
                    </Box>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Add Prefix Dialog */}
        <Dialog open={isAddPrefixOpen} onClose={() => !prefixSubmitting && setIsAddPrefixOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle style={{ fontWeight: "bold" }}>Add Stream Prefix</DialogTitle>
          <DialogContent dividers>
            <p style={{ fontSize: "13px", color: "#666", marginTop: 0, marginBottom: "16px" }}>
              Configure a stream prefix for cameras. This prefix will dynamically route <strong>Video + Audio</strong>, <strong>Thermal</strong>, and <strong>Audio</strong> streams (e.g. <code>Prefix-001</code>).
            </p>
            <TextField
              autoFocus
              margin="dense"
              label="Prefix Name"
              placeholder="e.g. Pole, Junction, Cam"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={newPrefixName}
              onChange={(e) => setNewPrefixName(e.target.value)}
              helperText="Alphanumeric, hyphen or underscore only"
              style={{ marginBottom: "14px" }}
            />
            <TextField
              margin="dense"
              label="Display Label (Optional)"
              placeholder="e.g. Pole Camera"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={newPrefixLabel}
              onChange={(e) => setNewPrefixLabel(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAddPrefixOpen(false)} disabled={prefixSubmitting}>
              Cancel
            </Button>
            <MDButton
              onClick={handleAddPrefix}
              variant="gradient"
              color="info"
              disabled={!newPrefixName.trim() || prefixSubmitting}
            >
              {prefixSubmitting ? "Saving..." : "Add Prefix"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Manage Prefixes Dialog */}
        <Dialog open={isManagePrefixOpen} onClose={() => setIsManagePrefixOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle style={{ fontWeight: "bold" }}>Manage Stream Prefixes</DialogTitle>
          <DialogContent dividers style={{ padding: "8px 16px" }}>
            <Box display="flex" flexDirection="column" gap={1}>
              {prefixesList.map((p) => (
                <Box
                  key={p.prefix_id || p.prefix_name}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p="8px 12px"
                  bgcolor="#f8f9fa"
                  borderRadius="8px"
                  border="1px solid #e0e0e0"
                >
                  <Box>
                    <MDTypography variant="subtitle2" fontWeight="bold" color="info">
                      {p.prefix_name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {p.label || p.prefix_name} &bull; Stream: {p.prefix_name}-{formattedDevId}
                    </MDTypography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {p.is_default ? (
                      <Chip label="Default" size="small" variant="outlined" sx={{ fontSize: "10px", height: "20px" }} />
                    ) : (
                      <Tooltip title="Delete prefix">
                        <IconButton size="small" color="error" onClick={() => handleDeletePrefix(p)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsManagePrefixOpen(false);
                setIsAddPrefixOpen(true);
              }}
            >
              Add New
            </MDButton>
            <Button onClick={() => setIsManagePrefixOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Fullscreen / Popout VLC Modal */}
        <LiveStreamModal
          isOpen={vlcModalOpen}
          onClose={() => setVlcModalOpen(false)}
          deviceId={targetDeviceId}
          deviceName={selectedCamObj?.name || selectedCamId}
          streamPrefix={streamPrefix}
        />
      </MDBox>
    </DashboardLayout>
  );
}

export default Cameras;
